"use client";

import { Button } from "@hisani/ui/button";
import { CloudOff, Download, ShieldAlert } from "lucide-react";
import { usePathname } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import {
  getPwaReloadBlocked,
  subscribePwaReloadBlocked,
} from "./pwa-reload-safety";

export function PwaManager({
  enabled = process.env.NODE_ENV === "production",
}: {
  readonly enabled?: boolean;
}) {
  const t = useTranslations("Common");
  const locale = useLocale();
  const pathname = usePathname();
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(
    null,
  );
  const [registrationFailed, setRegistrationFailed] = useState(false);
  const [activating, setActivating] = useState(false);
  const reloadRequested = useRef(false);
  const online = useSyncExternalStore(
    subscribeOnlineStatus,
    getOnlineStatus,
    () => true,
  );
  const reloadBlocked = useSyncExternalStore(
    subscribePwaReloadBlocked,
    getPwaReloadBlocked,
    () => false,
  );
  const normalizedPath = pathname.replace(/\/+$/u, "") || "/";
  const strippedPath = normalizedPath.startsWith(`/${locale}`)
    ? normalizedPath.slice(locale.length + 1) || "/"
    : normalizedPath;
  const adminOwnsOfflineState =
    strippedPath === "/admin" || strippedPath.startsWith("/admin/");
  const stopwatchOwnsOfflineState =
    strippedPath === "/stopwatch" || strippedPath.startsWith("/stopwatch/");

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (!("serviceWorker" in navigator)) {
      const unavailableNotice = window.setTimeout(
        () => setRegistrationFailed(true),
        0,
      );
      return () => window.clearTimeout(unavailableNotice);
    }

    let disposed = false;
    const handleControllerChange = () => {
      if (reloadRequested.current) {
        window.location.reload();
      }
    };
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      handleControllerChange,
    );

    navigator.serviceWorker
      .register("/serwist/sw.js", { scope: "/", type: "module" })
      .then((registration) => {
        if (disposed) {
          return;
        }
        setRegistrationFailed(false);
        if (registration.waiting !== null) {
          setWaitingWorker(registration.waiting);
        }
        registration.addEventListener("updatefound", () => {
          const worker = registration.installing;
          worker?.addEventListener("statechange", () => {
            if (
              !disposed &&
              worker.state === "installed" &&
              navigator.serviceWorker.controller !== null
            ) {
              setWaitingWorker(worker);
            }
          });
        });
      })
      .catch(() => {
        if (!disposed) {
          setRegistrationFailed(true);
        }
      });

    return () => {
      disposed = true;
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        handleControllerChange,
      );
    };
  }, [enabled]);

  const activateUpdate = useCallback(() => {
    if (waitingWorker === null || reloadBlocked || activating) {
      return;
    }
    reloadRequested.current = true;
    setActivating(true);
    waitingWorker.postMessage({ type: "HISANI_SKIP_WAITING" });
  }, [activating, reloadBlocked, waitingWorker]);

  if (waitingWorker !== null) {
    return (
      <aside
        className="pwa-status-notice fixed inset-x-3 bottom-[calc(var(--mobile-bottom-navigation-height)+var(--mobile-bottom-navigation-bottom-spacing)+12px)] mx-auto flex max-w-2xl flex-col gap-3 rounded-md border border-border-default bg-surface p-4 shadow-elevation-2 sm:flex-row sm:items-center sm:justify-between md:bottom-3"
        aria-live="polite"
      >
        <div className="flex gap-3">
          <Download
            className="mt-0.5 size-5 shrink-0 text-brand-ink"
            aria-hidden="true"
          />
          <div>
            <p className="font-bold text-primary">{t("pwaUpdateTitle")}</p>
            <p className="mt-1 text-sm leading-5 text-secondary">
              {reloadBlocked
                ? t("pwaUpdateBlocked")
                : t("pwaUpdateDescription")}
            </p>
          </div>
        </div>
        <Button
          variant="primary"
          onClick={activateUpdate}
          disabled={reloadBlocked || activating}
        >
          {activating ? t("pwaUpdating") : t("pwaUpdateAction")}
        </Button>
      </aside>
    );
  }

  if (!online && !adminOwnsOfflineState) {
    return (
      <PwaStatusNotice
        icon={<CloudOff className="size-5" aria-hidden="true" />}
        message={t(
          stopwatchOwnsOfflineState ? "pwaOffline" : "pwaOfflineGeneral",
        )}
      />
    );
  }

  if (registrationFailed) {
    return (
      <PwaStatusNotice
        icon={<ShieldAlert className="size-5" aria-hidden="true" />}
        message={t("pwaUnavailable")}
      />
    );
  }

  return null;
}

function subscribeOnlineStatus(onStoreChange: () => void): () => void {
  window.addEventListener("online", onStoreChange);
  window.addEventListener("offline", onStoreChange);
  return () => {
    window.removeEventListener("online", onStoreChange);
    window.removeEventListener("offline", onStoreChange);
  };
}

function getOnlineStatus(): boolean {
  return navigator.onLine;
}

function PwaStatusNotice({
  icon,
  message,
}: {
  readonly icon: React.ReactNode;
  readonly message: string;
}) {
  return (
    <p
      className="pwa-status-notice fixed inset-x-3 bottom-[calc(var(--mobile-bottom-navigation-height)+var(--mobile-bottom-navigation-bottom-spacing)+12px)] z-40 mx-auto flex min-h-11 max-w-xl items-center justify-center gap-2 rounded-md border border-border-default bg-surface px-4 py-2 text-center text-sm font-semibold text-secondary shadow-elevation-2 md:bottom-3"
      role="status"
    >
      <span className="text-brand-ink">{icon}</span>
      {message}
    </p>
  );
}
